import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ProductComplianceTechnicalHeadingTypeEnum } from '@inter-ikea-kompis/enums';
import { FontStyle } from '@inter-ikea-kompis/themes';
import type { ILocalizedInformation, IProduct, ITheme } from '@inter-ikea-kompis/types';
import { ProductUtility } from '@inter-ikea-kompis/utilities';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import { FocusOutline } from '@inter-ikea-kompis/component-focus-outline';
import { KeyboardInjectorFinder } from '@inter-ikea-kompis/component-keyboard';

import {
	ModalFooter as SkapaModalFooter,
	ModalHeader as SkapaModalHeader,
	Prompt as SkapaPrompt,
	Sheet as SkapaSheet
} from '@ingka/modal-webc/passive.js';

import WaterEfficiencyClassMediaEnum from '../../enums/WaterEfficiencyClassMediaEnum.js';
import WaterEfficiencyClassModalEnum from '../../enums/WaterEfficiencyClassModalEnum.js';
import type IWaterEfficiencyClassTranslations from '../../types/IWaterEfficiencyClassTranslations.js';
import type WaterEfficiencyClassCard from '../water-label-card/WaterLabelCard.js';
import WaterLabelCard from '../water-label-card/WaterLabelCard.js';
import WaterEfficiencyClassStyle from './WaterEfficiencyClassStyle.js';

/* eslint-disable lit-a11y/alt-text */

/**
 *  This is a component that helps communicating that a product is fulfilling local requirements for water-efficiency certifications.
 *
 * @event modalOpen Triggered when opening a modal
 * @event modalClose Triggered when closing a modal
 */

export default class WaterEfficiencyClass extends KompisElement {
	// A product.
	@property({ type: Object, required: true })
	public product!: IProduct;

	// Theme.
	@property({ type: Object, required: true })
	public theme!: ITheme;

	// Localized information. Deprecated - use "translations" instead.
	@property({ type: Object })
	public localizedInformation: ILocalizedInformation | null = null;

	// The translations retrieved from translations service.
	@property({ type: Object })
	public translations: IWaterEfficiencyClassTranslations | null = null;

	// Visible modal.
	@property({ type: String })
	public visibleModal: WaterEfficiencyClassModalEnum | null = null;

	private sheetElement: SkapaSheet | null = null;

	/**
	 * Renders the water label.
	 */
	public override render(): RenderResult {
		// We want translations to be required, but since both props to provide translations are optional, this check is needed until localizedInformation is removed.
		if (!this.hasTranslations()) {
			// We can't throw an error because on the first render no props are set.
			return null;
		}

		return this.getWaterImages();
	}

	public override disconnectedCallback(): void {
		super.disconnectedCallback();
		this.closeSheet();
	}

	public override async connectedCallback(): Promise<void> {
		this.customElements.define('skapa-sheet', SkapaSheet);
		this.customElements.define('skapa-modal-footer', SkapaModalFooter);
		this.customElements.define('skapa-modal-header', SkapaModalHeader);
		this.customElements.define('skapa-prompt', SkapaPrompt);
		this.customElements.define('kompis-focus-outline', FocusOutline);
		this.customElements.define('kompis-water-label-card', WaterLabelCard);

		super.connectedCallback();
	}

	protected override shouldUpdate(
		changedProperties: Map<string, object | number | string | boolean>
	): boolean {
		if (changedProperties.has('visibleModal')) {
			if (this.visibleModal) {
				this.openSheet();
			} else {
				this.closeSheet();
			}
		}
		return true;
	}

	private hasTranslations(): boolean {
		return !!this.translations || !!this.localizedInformation?.translations;
	}

	private getTranslations(): IWaterEfficiencyClassTranslations {
		const translations = this.translations
			? this.translations
			: this.localizedInformation?.translations;

		return <IWaterEfficiencyClassTranslations>translations;
	}

	private getWaterImages(): RenderResult {
		const imageUrl = this.getImageUrl();

		if (imageUrl !== null) {
			return this.showWaterImage(imageUrl);
		}

		return html`
			<style>
				:host {
					display: none;
				}
			</style>
		`;
	}

	/**
	 * Extract image URL from product.
	 */
	private getImageUrl(): string | null {
		const waterEfficiencyClass = this.product.content?.complianceTechnical?.find(
			(complianceTechnicalItem) =>
				complianceTechnicalItem.headingType ===
				ProductComplianceTechnicalHeadingTypeEnum.unifiedWaterLabel
		);

		if (!waterEfficiencyClass) {
			return null;
		}

		const find = (fileTypeCode: WaterEfficiencyClassMediaEnum): string => {
			return (
				waterEfficiencyClass.media?.find((mediaType) => mediaType.fileTypeCode === fileTypeCode)
					?.url || ''
			);
		};

		const url = find(WaterEfficiencyClassMediaEnum.svg) ?? find(WaterEfficiencyClassMediaEnum.jpeg);
		return url || null;
	}

	/**
	 * Renders the water label image.
	 *
	 * @param imageUrl ...
	 */
	private showWaterImage(imageUrl: string): RenderResult {
		return html`
			<style>
				${WaterEfficiencyClassStyle.getStyle()}
			</style>
			<kompis-focus-outline .theme="${this.theme}">
				<img
					tabindex="0"
					role="button"
					aria-label="${this.getImageAlt()}"
					@keydown="${(event: KeyboardEvent) => {
						if (event.key === 'Enter' || event.key === ' ') {
							this.sendModalOpen();
						}
					}}"
					@click="${(event: MouseEvent) => {
						event.preventDefault();
						event.stopPropagation();
						this.sendModalOpen();
					}}"
					src="${imageUrl}"
				/>
			</kompis-focus-outline>
		`;
	}

	private sendModalOpen(): void {
		const triggerElement = this.queryTriggerElement();
		this.dispatchEvent(
			new CustomEvent('modalOpen', {
				bubbles: true,
				detail: { visibleModal: WaterEfficiencyClassModalEnum.waterLabelCard, triggerElement }
			})
		);
	}

	private closeSheet(): void {
		if (this.sheetElement) {
			this.sheetElement.open = false;
			this.sheetElement = null;
		}
	}

	private openSheet(): void {
		if (this.sheetElement) {
			return;
		}

		const sheetElement: SkapaSheet = this.createSheetElement();
		this.sheetElement = sheetElement;

		const parent = KeyboardInjectorFinder.findKeyboardInjectorElement() || document.body;
		parent.appendChild(sheetElement);

		sheetElement.open = true;
	}

	private createSheetElement(): SkapaSheet {
		const sheetElement = this.createSheet();
		const sheetHeaderElement = this.createSheetHeader();
		const cardElement = this.createCard();

		sheetElement.appendChild(sheetHeaderElement);
		sheetElement.appendChild(cardElement);
		return sheetElement;
	}

	private createSheet(): SkapaSheet {
		const sheetElement = this.createInternalElement('skapa-sheet');
		sheetElement.size = 'small';
		sheetElement.fullHeight = true;
		sheetElement.style.fontFamily = FontStyle.getFontFamily(this.theme);
		sheetElement.style.zIndex = '500';
		sheetElement.addEventListener('closerequest', () => {
			if (this.sheetElement) {
				this.dispatchEvent(
					new CustomEvent('modalClose', {
						bubbles: true,
						detail: {
							visibleModal: null,
							triggerElement: this.queryTriggerElement()
						}
					})
				);
			}
		});
		sheetElement.addEventListener('closed', () => {
			sheetElement.parentElement?.removeChild(sheetElement);
		});

		return sheetElement;
	}

	private createSheetHeader(): SkapaModalHeader {
		const sheetHeaderElement = this.createInternalElement('skapa-modal-header');
		sheetHeaderElement.slot = 'header';

		const headerSpan = document.createElement('span');
		headerSpan.slot = 'closebutton-label';
		headerSpan.innerText = this.getTranslations().modalAriaLabelButtonClose;

		sheetHeaderElement.appendChild(headerSpan);

		return sheetHeaderElement;
	}

	private createCard(): WaterEfficiencyClassCard {
		const cardElement = this.createInternalElement('kompis-water-label-card');
		cardElement.theme = this.theme;
		cardElement.product = this.product;
		cardElement.localizedInformation = this.localizedInformation;

		return cardElement;
	}

	private queryTriggerElement(): HTMLImageElement {
		return <HTMLImageElement>this.shadowRoot?.querySelector('img');
	}

	/**
	 * Returns corresponding text to the water efficiency class image.
	 */
	private getImageAlt(): string {
		const complianceType = ProductUtility.getProductComplianceType(this.product);

		switch (complianceType) {
			case ProductComplianceTechnicalHeadingTypeEnum.unifiedWaterLabel: {
				const eecObject = this.product.content?.complianceTechnical?.find(
					(obj) => obj.headingType === complianceType
				);
				const { headingText, text } = eecObject || {};

				return `${headingText} ${text}`;
			}
			default:
				return '';
		}
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'kompis-water-efficiency-class': WaterEfficiencyClass;
		'skapa-sheet': SkapaSheet;
		'skapa-modal-footer': SkapaModalFooter;
		'skapa-modal-header': SkapaModalHeader;
		'skapa-prompt': SkapaPrompt;
	}
}
